import React, { createContext, ReactNode, useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import * as vp from 'vopay';
import { useTranslation } from 'react-i18next';
import useStorage from '../hooks/useStorage';
import { useBank } from './Bank.provider';
import { useDataDog } from './Datadog.provider';
import useFetchiQ11TokenInfo from '../hooks/useApi/useFetchiQ11TokenInfo';
import useFetchFinancialInstitutions from '../hooks/useApi/useFetchFinancialInstitutions';
import { useAccessTokenFields } from './AccessToken.provider';
import { useDataFetchIndicator } from '../hooks';
import 'vopay/theme.scss';

export type TokenCtx = {
  token: vp.TokenInfoResponse | undefined;
  isProcessing: boolean;
  isSetByBrowser: boolean | undefined;
  iQ11ForOnboarding: boolean | undefined;
  sessionToken: string | undefined;
  setToken: (token: string) => void;
  reset: () => void;
};

export const TokenContext = createContext<TokenCtx>({
  token: {
    Currency: 'CAD',
    Success: false,
    ErrorMessage: '',
    AccountSelectionMethod: '',
    AccountHolderType: '',
    BGColor: '',
    ClientName: '',
    ConsentMessage: '',
    Country: '',
    FlinksInstitutionID: 0,
    ForegroundColor: '',
    Language: '',
    PaymentSelectionMethod: '',
    RedirectMethod: '',
    RedirectURL: '',
    TermsAndConditions: '',
    HasLogin: true,
    WithTransactions: false,
    Version: '',
    DebitAuthority: '',
    RequireDebitAuthorityAgreement: false,
    BrandingDetails: {},
    DarkMode: false,
    Trigger3DS: false,
    JWT: '',
    AgreementURL: '',
    CardTypeValidation: false,
    AcceptedCardBrands: [],
    DebitAgreementBusinessEnUrl: '',
    DebitAgreementBusinessFrUrl: '',
    DebitAgreementIndividualEnUrl: '',
    DebitAgreementIndividualFrUrl: '',
    PrivacyPolicyEnURL: '',
    PrivacyPolicyFrURL: '',
    TermsOfServiceEnURL: '',
    TermsOfServiceFrURL: '',
  },
  isProcessing: false,
  isSetByBrowser: false,
  iQ11ForOnboarding: false,
  sessionToken: '',

  reset: () => {},
  setToken: () => {},
});
function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}
export const TokenProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const [ForOnboarding, setForOnboarding] = useState(false);
  const { setFinancialInstitution, setBankingType, setMainFinancialInstitution } = useBank();
  const searchParms = useQuery();
  const [isProcessing, setIsProcessing] = useState(true);
  const { i18n, t } = useTranslation('translations', { keyPrefix: 'Common' });
  const [, setLoading] = useDataFetchIndicator();
  const [, , , fetchIQ11TokenInfoAsync] = useFetchiQ11TokenInfo({});
  const [, , , fetchFinancialInstitutionsAsync] = useFetchFinancialInstitutions({});
  const [sessionToken, setSessionToken] = useState('');
  const storage = useStorage();
  const { iQ11TokenInfo, iQ11IsBrowser: isIQ11, iQ11Token, iQ11ForOnboarding } = storage.token.getIQ11Token();
  const { setUser, setService } = useDataDog();
  const history = useHistory();
  const { accessToken } = useAccessTokenFields();
  const { clearBank } = storage.bank;
  const { clearAccessTokenFields } = storage.accessToken;
  const { clearIQ11Token } = storage.token;
  const { clearJWT } = storage.JWT;
  const { clearPadInfo } = storage.padAgreement;
  const { clearPaymentMethod } = storage.paymentMethod;

  useEffect(() => {
    const forOnboarding = !!searchParms.get('onboarding');
    const lang = searchParms.get('Language') || '';
    const iframeKey = searchParms.get('IframeKey');

    setForOnboarding(forOnboarding);

    if (!iframeKey) {
      setIsProcessing(false);
      return;
    }

    clearBank();
    clearAccessTokenFields();
    clearIQ11Token();
    clearJWT();
    clearPadInfo();
    clearPaymentMethod();

    setIsProcessing(true);
    const abortController = new AbortController();

    const initializeToken = async () => {
      setLoading(true, `${t('defaultLoading')}`, 'fullScreen');

      try {
        storage.token.storeIQ11Token(iframeKey, true, forOnboarding);

        const tokenResult = await fetchIQ11TokenInfoAsync({ ForOnboarding: forOnboarding });

        if (abortController.signal.aborted) return;

        if (tokenResult.JWT) {
          storage.JWT.storeJWT(tokenResult.JWT);
        }

        const logo = tokenResult?.BrandingDetails?.Logo || '';
        storage.token.storeIQ11TokenResponse({
          ...tokenResult,
          BrandingDetails: { ...tokenResult.BrandingDetails, Logo: logo },
        });

        if (tokenResult.Success) {
          i18n.changeLanguage(lang !== '' ? lang : tokenResult.Language);

          if (tokenResult.DarkMode) {
            setDarkMode();
          }

          if (tokenResult.HasLogin) {
            history.push('/');
            setIsProcessing(false);
          } else if (tokenResult.FlinksInstitutionID) {
            const financialResult = await fetchFinancialInstitutionsAsync({ Country: tokenResult.Country });

            if (abortController.signal.aborted) return;

            if (financialResult.Success) {
              const FilteredBankList = Object.values(financialResult.FinancialInstitutions)
                .map((Institution) => {
                  const HasMoreThanOne =
                    Institution.FinancialInstitutionID.toString() === '1833' ||
                    Institution.FinancialInstitutionID.toString() === '1692';
                  if (Institution.FinancialInstitutionID.toString() === '1833') {
                    return { ...Institution, HasMoreThanOne } as vp.FinancialInstitutionInfo;
                  }
                  return { ...Institution, HasMoreThanOne } as vp.FinancialInstitutionInfo;
                })
                .filter(
                  (bank) =>
                    bank?.ExternalFIID === tokenResult.FlinksInstitutionID.toString() ||
                    bank?.AssociatedExternalFIIDs.split(',').includes(tokenResult.FlinksInstitutionID.toString()),
                );

              const newfinancialInstitution = {
                ...FilteredBankList[0],
                ExternalFIID: tokenResult.FlinksInstitutionID.toString(),
              };
              setBankingType('online');
              setFinancialInstitution(newfinancialInstitution);
              setMainFinancialInstitution(FilteredBankList[0]);
              history.push(`/connect/bank/${FilteredBankList[0].FinancialInstitutionID}/online`);
              setIsProcessing(false);
            }
          } else if (tokenResult.PaymentSelectionMethod === 'bank') {
            history.push('/connect/bank');
            setIsProcessing(false);
          } else if (tokenResult.PaymentSelectionMethod === 'credit') {
            history.push({ pathname: '/connect/processor/credit', state: { origin: 'iframe' } });
            setSessionToken(accessToken);
            setIsProcessing(false);
          } else if (tokenResult.PaymentSelectionMethod === 'debitcard') {
            history.push({ pathname: '/connect/processor/debitcard', state: { origin: 'iframe' } });
          } else if (tokenResult.PaymentSelectionMethod === 'email') {
            history.push({ pathname: '/connect/processor/email', state: { origin: 'iframe' } });
          } else if (tokenResult.PaymentSelectionMethod === 'paypal') {
            history.push({
              pathname: '/connect/digital-wallet/paypal',
              state: { origin: 'iframe' },
            });
          } else if (tokenResult.PaymentSelectionMethod === 'venmo') {
            history.push({
              pathname: '/connect/digital-wallet/venmo',
              state: { origin: 'iframe' },
            });
          } else if (tokenResult.PaymentSelectionMethod === 'zelle') {
            history.push({
              pathname: '/connect/digital-wallet/zelle',
              state: { origin: 'iframe' },
            });
          } else {
            history.push({
              pathname: '/connect',
              state: { paymentMethod: tokenResult.PaymentSelectionMethod, origin: 'iframe' },
            });
            setSessionToken(accessToken);
            setIsProcessing(false);
          }
        } else {
          history.push('/error');
        }
      } finally {
        setLoading(false);
      }
    };

    void initializeToken();

    return () => {
      abortController.abort();
    };
  }, [searchParms]);

  useEffect(() => {
    if (storage.token.getIQ11Token().iQ11TokenInfo?.DarkMode) {
      setDarkMode();
    }
  }, []);

  useEffect(() => {
    if (!iQ11TokenInfo || !iQ11TokenInfo.Success || !isIQ11) return;
    i18n.changeLanguage(iQ11TokenInfo.Language);
    vp.setTitle(!!iQ11TokenInfo.PageTitle && iQ11TokenInfo.PageTitle !== '' ? iQ11TokenInfo.PageTitle : 'eLinx');
    vp.setFavicon(
      !!iQ11TokenInfo.Favicon && iQ11TokenInfo.Favicon !== ''
        ? iQ11TokenInfo.Favicon
        : 'src/assets/images/favicon/elinx-favicon.png',
    );
  }, [iQ11TokenInfo, isIQ11]);

  useEffect(() => {
    if (isIQ11) {
      setUser({
        IframeKey: iQ11Token,
      });
      setService('iq11');
    }
  }, [iQ11Token, isIQ11]);

  function setDarkMode() {
    document.documentElement.style.setProperty('--secondary-button-color', '#2D2D2D');
    document.documentElement.style.setProperty('--secondary-button-font-color', '#fbfbfb');
    document.documentElement.style.setProperty('--disabled-button-color', '#7D692D');
    document.documentElement.style.setProperty('--disabled-button-font-color', '#2d2d2d');
    document.documentElement.style.setProperty('--background-color', '#1A1A1A');
    document.documentElement.style.setProperty('--primaryFontColor', '#FBFBFB');
    document.documentElement.style.setProperty('--secondaryFontColor', '#DEDEDE');
    document.documentElement.style.setProperty('--background-color-light', '#363636');
  }

  const setTokenFunction = (newToken: string) => {
    if (!newToken) return;

    const forOnboarding = iQ11ForOnboarding || ForOnboarding || false;
    storage.token.storeIQ11Token(newToken, false, forOnboarding);

    void (async () => {
      setLoading(true, `${t('defaultLoading')}`, 'fullScreen');

      try {
        const tokenResult = await fetchIQ11TokenInfoAsync({ ForOnboarding: forOnboarding });

        if (tokenResult.JWT) {
          storage.JWT.storeJWT(tokenResult.JWT);
        }

        const logo = tokenResult?.BrandingDetails?.Logo || '';
        storage.token.storeIQ11TokenResponse({
          ...tokenResult,
          BrandingDetails: { ...tokenResult.BrandingDetails, Logo: logo },
        });

        if (tokenResult.Success) {
          i18n.changeLanguage(tokenResult.Language);
        }
      } finally {
        setLoading(false);
      }
    })();
  };
  return (
    <TokenContext.Provider
      value={{
        token: iQ11TokenInfo,
        isProcessing,
        isSetByBrowser: isIQ11,
        iQ11ForOnboarding,
        sessionToken,
        reset: () => storage.token.clearIQ11Token(),
        setToken: setTokenFunction,
      }}
    >
      {children}
    </TokenContext.Provider>
  );
};

export const useToken = (): TokenCtx => React.useContext(TokenContext);
